import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Calendar} from "primereact/calendar";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import Swal from "sweetalert2";
import {deleteActa} from "./integracion";

const TablaActasAdmin = ({data = [], onSave, chofer, options, fetchActas}) => {
    // Local dialog state
    const [visible, setVisible] = useState(false);
    const [selectedActa, setSelectedActa] = useState(null);
    const [mode, setMode] = useState("create"); // "create" | "edit"
    const [form, setForm] = useState({
        ID: null,
        NOMBRE: "",
        FECHA_ACTA: null,
        NUM_ACTA: "",
        MOTIVO: ""
    });

    useEffect(() => {
        if (options && options.length > 0) {
            const exists = options.some(o => o.ID === 1);
            setSelectedActa(prev => (prev == null && exists ? 1 : prev ?? 1));
        }
    }, [options]);
    const dtRef = useRef(null);

    const parsedData = useMemo(() => {
        const parseDate = (v) => {
            if (!v) return null;
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
        };
        return (data || []).map(r => ({
            ...r,
            _FECHA_DATE: parseDate(r.FECHA_ACTA)
        }));
    }, [data]);

    // Export helpers
    const columns = useMemo(() => ([
        { field: "FECHA_ACTA", header: "FECHA", render: (r) => r._FECHA_DATE ? r._FECHA_DATE.toLocaleDateString('es-MX') : "" },
        { field: "NUM_ACTA", header: "NUMERO" },
        { field: "MOTIVO", header: "MOTIVO" },
        { field: "NOMBRE", header: "NOMBRE" },
        { field: "ID", header: "ID" }
    ]), []);

    const safeUser = (chofer?.NOMBRE || "usuario").trim?.().replace?.(/[\\/:*?"<>|]+/g, "") || "usuario";
    const baseTitle = `Actas Administrativas - ${safeUser}`;
    const baseFile = `actas_${safeUser}`;

    const buildPlainHtmlTable = (cols, rows) => {
        const thead = `<thead><tr>${cols.map(c => `<th>${c.header}</th>`).join("")}</tr></thead>`;
        const tbody = `<tbody>${
            (rows || [])
                .map(r => `<tr>${cols.map(c => {
                    const val = typeof c.render === "function" ? c.render(r) : (r?.[c.field] ?? "");
                    return `<td>${val}</td>`;
                }).join("")}</tr>`).join("")
        }</tbody>`;
        return `<table>${thead}${tbody}</table>`;
    };

    const printTable = () => {
        const style = `
      <style>
        table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f5f5f5; }
        h2 { font-family: Arial, sans-serif; }
      </style>
    `;
        const title = `<h2>${baseTitle}</h2>`;
        const tableHtml = buildPlainHtmlTable(columns, parsedData);
        const win = window.open("", "PRINT", "height=700,width=1000");
        if (win) {
            win.document.write(style + title + tableHtml);
            win.document.close();
            win.focus();
            win.print();
        }
    };

    const exportCSV = () => {
        dtRef.current?.exportCSV({ selectionOnly: false, exportHeader: true, filename: baseFile });
    };

    const exportPDF = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });
        const head = [columns.map(c => c.header)];
        const body = (parsedData || []).map(row => columns.map(c => {
            if (typeof c.render === "function") return c.render(row);
            return row?.[c.field] ?? "";
        }));

        doc.text(baseTitle, 40, 40);
        autoTable(doc, {
            startY: 60,
            head,
            body,
            styles: { fontSize: 8, cellPadding: 4 },
            headStyles: { fillColor: [33, 150, 243] }
        });
        doc.save(`${baseFile}.pdf`);
    };

    const openCreate = () => {
        setMode("create");
        setForm({
            ID: null,
            NOMBRE: chofer?.NOMBRE || "",
            FECHA_ACTA: null,
            NUM_ACTA: "",
            MOTIVO: ""
        });
        setVisible(true);
    };

    const onEdit = (row) => {
        setMode("edit");
        setForm({
            ID: row?.ID ?? row?.id ?? null, // soporta diferentes claves
            NOMBRE: row?.NOMBRE ?? row?.nombre ?? "",
            FECHA_ACTA: row?._FECHA_DATE ?? null,
            NUM_ACTA: row?.NUM_ACTA ?? row?.num_acta ?? "",
            MOTIVO: row?.MOTIVO ?? row?.motivo ?? ""
        });
        setVisible(true);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        console.log("acta", selectedActa)
        const payload = {
            ID: form.ID ?? null,
            NOMBRE: form.NOMBRE,
            NUM_ACTA: form.NUM_ACTA,
            ID_ACTA: selectedActa,
            MOTIVO: form.MOTIVO,
            FECHA_ACTA: form.FECHA_ACTA instanceof Date ? form.FECHA_ACTA.toISOString() : null,
            _mode: mode
        };
        onSave && onSave(payload);
        setVisible(false);
    };

    const header = (
        <div style={{ display: "flex", gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button icon="pi pi-file-plus" label="Agregar acta" style={{ backgroundColor: "#008a78"}} onClick={openCreate}/>
            {/* Export/print actions */}
            <Button icon="pi pi-file-excel" label="Excel" onClick={exportCSV} style={{ backgroundColor: "#236003"}}/>
            <Button icon="pi pi-file-pdf" label="PDF" onClick={exportPDF} className="p-button-danger" />
            <Button icon="pi pi-print" onClick={printTable} label="Imprimir" />
        </div>
    )

    const editBody = (row) => {
        const handleDelete = () => {
            Swal.fire({
                title: '¿Estás seguro de borrar el registro?',
                text: "No podrás revertir esta acción",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Handle delete action here
                    deleteActa(row.ID).then(r => {
                        if (r.success) {
                            Swal.fire({
                                icon: "success",
                                title: "Acta eliminada correctamente",
                                showConfirmButton: false,
                                timer: 1500,
                            });

                            fetchActas()
                        } else {
                            Swal.fire({
                                icon: "error",
                                title: "Error al eliminar el acta",
                                text: r.message || "Error desconocido",
                            });
                        }
                    })
                }
            });
        };

        return (
            <div className="action-buttons" style={{display: 'flex', gap: '8px'}}>
                <Button
                    label=""
                    rounded outlined
                    icon="pi pi-trash"
                    severity="danger"
                    onClick={handleDelete}
                />
                <Button
                    label=""
                    rounded outlined
                    icon="pi pi-pencil"
                    severity="warning"
                    onClick={() => onEdit(row)}
                />

            </div>
        )
    };

    const dialogFooter = (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button label="Cancelar" icon="pi pi-times" severity="secondary" onClick={() => setVisible(false)} />
            <Button label="Guardar" icon="pi pi-check" onClick={handleSave} />
        </div>
    );

    if (!data || data.length === 0) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160, flexDirection: "column", gap: 8, padding: 16}}>
                <Button icon="pi pi-file-plus" label="Agregar acta" style={{ backgroundColor: "#008a78"}} onClick={openCreate}/>

                <span style={{ color: "#777", fontWeight: 600 }}>Sin actas administrativas</span>
                <Dialog
                    header={mode === "edit" ? "Editar Acta" : "Nueva Acta"}
                    visible={visible}
                    modal
                    style={{ width: '600px', maxWidth: '95vw' }}
                    onHide={() => setVisible(false)}
                    footer={dialogFooter}
                >
                    <div style={{ display: "grid", gap: 12 }}>
                        {/* No editables */}
                        <div className="p-field">
                            <label className="p-d-block" style={{ fontWeight: 600 }}>ID</label>
                            <InputText value={form.ID ?? ""} disabled className="p-inputtext-sm" />
                        </div>
                        <div className="p-field">
                            <label className="p-d-block" style={{ fontWeight: 600 }}>Nombre</label>
                            <InputText value={form.NOMBRE ?? ""} disabled className="p-inputtext-sm" />
                        </div>

                        {/* Editables */}
                        <div className="p-field">
                            <Dropdown
                                value={selectedActa}
                                options={options || []}
                                optionLabel="TIPO"
                                optionValue="ID"
                                onChange={(e) => setSelectedActa(e.value)}
                                placeholder="Seleccionar tipo"
                                className="p-inputtext-sm"
                                style={{ fontSize: '12px' }}
                                disabled={!options || options.length === 0}
                            />
                        </div>
                        <div className="p-field">
                            <label className="p-d-block" style={{ fontWeight: 600 }}>Fecha</label>
                            <Calendar
                                value={form.FECHA_ACTA}
                                onChange={(e) => handleChange("FECHA_ACTA", e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm"
                            />
                        </div>
                        <div className="p-field">
                            <label className="p-d-block" style={{ fontWeight: 600 }}>Número de acta</label>
                            <InputNumber
                                value={form.NUM_ACTA}
                                onChange={(e) => handleChange("NUM_ACTA", e.value)}
                                className="p-inputtext-sm"
                            />
                        </div>
                        <div className="p-field">
                            <label className="p-d-block" style={{ fontWeight: 600 }}>Motivo</label>
                            <InputTextarea
                                value={form.MOTIVO}
                                onChange={(e) => handleChange("MOTIVO", e.target.value)}
                                rows={4}
                                autoResize
                                className="p-inputtext-sm"
                            />
                        </div>
                    </div>
                </Dialog>
            </div>


        );
    }
    
    return (
        <>
            <DataTable ref={dtRef} value={parsedData} header={header} emptyMessage="No se encontraron datos.">
                <Column header="" body={editBody} />
                <Column field="FECHA_ACTA" header="FECHA" />
                <Column field="NUM_ACTA" header="NUMERO" />
                <Column field="MOTIVO" header="MOTIVO" />
                <Column field="NOMBRE" header="NOMBRE" />
                <Column field="ID" header="ID" />
            </DataTable>
            <Dialog
                header={mode === "edit" ? "Editar Acta" : "Nueva Acta"}
                visible={visible}
                modal
                style={{ width: '600px', maxWidth: '95vw' }}
                onHide={() => setVisible(false)}
                footer={dialogFooter}
            >
                <div style={{ display: "grid", gap: 12 }}>
                    {/* No editables */}
                    <div className="p-field">
                        <label className="p-d-block" style={{ fontWeight: 600 }}>ID</label>
                        <InputText value={form.ID ?? ""} disabled className="p-inputtext-sm" />
                    </div>
                    <div className="p-field">
                        <label className="p-d-block" style={{ fontWeight: 600 }}>Nombre</label>
                        <InputText value={form.NOMBRE ?? ""} disabled className="p-inputtext-sm" />
                    </div>

                    {/* Editables */}
                    <div className="p-field">
                        <Dropdown
                            value={selectedActa}
                            options={options || []}
                            optionLabel="TIPO"
                            optionValue="ID"
                            onChange={(e) => setSelectedActa(e.value)}
                            placeholder="Seleccionar tipo"
                            className="p-inputtext-sm"
                            style={{ fontSize: '12px' }}
                            disabled={!options || options.length === 0}
                        />
                    </div>
                    <div className="p-field">
                        <label className="p-d-block" style={{ fontWeight: 600 }}>Fecha</label>
                        <Calendar
                            value={form.FECHA_ACTA}
                            onChange={(e) => handleChange("FECHA_ACTA", e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="p-inputtext-sm"
                        />
                    </div>
                    <div className="p-field">
                        <label className="p-d-block" style={{ fontWeight: 600 }}>Número de acta</label>
                        <InputNumber
                            value={form.NUM_ACTA}
                            onChange={(e) => handleChange("NUM_ACTA", e.value)}
                            className="p-inputtext-sm"
                        />
                    </div>
                    <div className="p-field">
                        <label className="p-d-block" style={{ fontWeight: 600 }}>Motivo</label>
                        <InputTextarea
                            value={form.MOTIVO}
                            onChange={(e) => handleChange("MOTIVO", e.target.value)}
                            rows={4}
                            autoResize
                            className="p-inputtext-sm"
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};
export default TablaActasAdmin